import React, { useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2 } from 'lucide-react';

/**
 * Wraps a route that requires *some* session.
 *   <ProtectedRoute>                  → redirects to /login if not authenticated
 *   <ProtectedRoute autoGuest>        → auto-creates a guest session, no redirect
 *
 * `autoGuest` is used on public landing pages (e.g. /personality/iq) where we
 * want incoming marketing traffic to see content immediately. Users can still
 * upgrade from the guest session to a real account from any purchase flow.
 */
export default function ProtectedRoute({ children, autoGuest = false }) {
  const { user, loading, loginAsGuest } = useAuth();
  const triggeredRef = useRef(false);

  useEffect(() => {
    if (autoGuest && !loading && !user && !triggeredRef.current) {
      triggeredRef.current = true;
      loginAsGuest();
    }
  }, [autoGuest, loading, user, loginAsGuest]);

  if (loading || (autoGuest && !user)) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg)' }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--primary)' }} />
      </div>
    );
  }

  // Allow both authenticated users and guest users
  if (!user) return <Navigate to="/login" replace />;

  return children;
}
